import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { supabase } from "../../lib/supabase";
import { useCategories } from "../../hooks/useCategories";
import { useApp } from "../../contexts/AppContext";

const PRICE_RANGES = [
  { label: "すべて", min: 0, max: Infinity },
  { label: "〜5,000円", min: 0, max: 5000 },
  { label: "5,001〜10,000円", min: 5001, max: 10000 },
  { label: "10,001〜20,000円", min: 10001, max: 20000 },
  { label: "20,001円〜", min: 20001, max: Infinity },
];

const SORTS = [
  { value: "popular", label: "人気順" },
  { value: "newest", label: "新着順" },
  { value: "price_asc", label: "価格：安い順" },
  { value: "price_desc", label: "価格：高い順" },
  { value: "rating", label: "評価順" },
];

const STOCK_FILTERS = [
  { value: "all", label: "すべて" },
  { value: "in_stock", label: "在庫あり" },
];

interface KitProductDB {
  id: string;
  title: string;
  description: string | null;
  price: number;
  thumbnail_url: string | null;
  category: string | null;
  sales_count: number;
  stock_count: number | null;
  created_at: string;
  provider_id: string;
  provider: { id: string; name: string; service_name: string | null; rating: number; review_count: number } | null;
}

export default function MaterialsPage() {
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPriceIdx, setSelectedPriceIdx] = useState(0);
  const [stockFilter, setStockFilter] = useState("all");
  const [sort, setSort] = useState("popular");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [products, setProducts] = useState<KitProductDB[]>([]);
  const [loading, setLoading] = useState(true);
  const { openReport } = useApp();
  const { categories } = useCategories();

  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("products")
        .select("id,title,description,price,thumbnail_url,category,sales_count,stock_count,created_at,provider_id,provider:provider_id(id,name,service_name,rating,review_count)")
        .eq("type", "kit")
        .eq("visibility", "public")
        .eq("status", "public")
        .order("sales_count", { ascending: false });
      setProducts((data || []) as KitProductDB[]);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (k) =>
          k.title.toLowerCase().includes(q) ||
          ((k.provider?.service_name || k.provider?.name) ?? "").toLowerCase().includes(q) ||
          (k.category || "").toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== "all") list = list.filter((k) => k.category === selectedCategory);
    const pr = PRICE_RANGES[selectedPriceIdx];
    list = list.filter((k) => k.price >= pr.min && k.price <= pr.max);
    if (stockFilter === "in_stock") list = list.filter((k) => (k.stock_count ?? 0) > 0);
    if (sort === "popular") list.sort((a, b) => b.sales_count - a.sales_count);
    else if (sort === "newest") list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    else if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, search, selectedCategory, selectedPriceIdx, stockFilter, sort]);

  const getProviderName = (p: KitProductDB) => p.provider?.service_name || p.provider?.name || "事業者";

  return (
    <div className={`min-h-screen flex flex-col bg-ivory transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
      <Navbar />
      <section className="relative pt-16 overflow-hidden">
        <div className="h-48 md:h-56 relative flex items-center justify-center overflow-hidden">
          <img src="https://public.readdy.ai/ai/img_res/7011de87708c1691df22ec78033cd453.jpg"
            className="absolute inset-0 w-full h-full object-cover object-center" alt="" />
          <div className="absolute inset-0 bg-deep-green/70" />
          <div className="relative text-center px-4">
            <p className="font-display italic text-gold text-xs tracking-widest mb-3">Teaching Materials</p>
            <h1 className="font-serif text-white text-3xl md:text-4xl font-light mb-2">教材キット一覧</h1>
            <p className="text-white/70 text-sm font-sans">職人監修の本物の道具・素材キットで実践力を高めましょう</p>
          </div>
        </div>
      </section>

      <main className="max-w-screen-xl mx-auto w-full px-4 md:px-8 py-10 flex-1">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="教材名・事業者名・カテゴリで検索"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 text-sm font-sans bg-white focus:outline-none focus:border-teal transition-colors" />
          </div>
          <div className="flex gap-2">
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="px-4 py-3 rounded-xl border border-stone-200 text-sm font-sans bg-white cursor-pointer focus:outline-none focus:border-teal">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <div className="flex rounded-xl border border-stone-200 overflow-hidden">
              <button onClick={() => setViewMode("grid")}
                className={`w-10 h-full flex items-center justify-center cursor-pointer transition-colors ${viewMode === "grid" ? "bg-deep-green text-white" : "bg-white text-charcoal/40"}`}>
                <i className="ri-grid-line text-sm" />
              </button>
              <button onClick={() => setViewMode("list")}
                className={`w-10 h-full flex items-center justify-center cursor-pointer transition-colors ${viewMode === "list" ? "bg-deep-green text-white" : "bg-white text-charcoal/40"}`}>
                <i className="ri-list-unordered text-sm" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-24 space-y-6">
              <div>
                <p className="text-xs font-sans text-charcoal/40 uppercase tracking-widest mb-3">カテゴリ</p>
                <div className="space-y-1">
                  <button onClick={() => setSelectedCategory("all")}
                    className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${selectedCategory === "all" ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                    すべて
                  </button>
                  {categories.map((c) => (
                    <button key={c.id} onClick={() => setSelectedCategory(c.name)}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${selectedCategory === c.name ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-sans text-charcoal/40 uppercase tracking-widest mb-3">価格帯</p>
                <div className="space-y-1">
                  {PRICE_RANGES.map((pr, i) => (
                    <button key={pr.label} onClick={() => setSelectedPriceIdx(i)}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${selectedPriceIdx === i ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                      {pr.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-sans text-charcoal/40 uppercase tracking-widest mb-3">在庫</p>
                <div className="space-y-1">
                  {STOCK_FILTERS.map((s) => (
                    <button key={s.value} onClick={() => setStockFilter(s.value)}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${stockFilter === s.value ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm font-sans text-charcoal/50">
                {loading ? "読み込み中..." : <><span className="text-charcoal font-medium">{filtered.length}</span> 件の教材キット</>}
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-stone-100 overflow-hidden animate-pulse">
                    <div className="h-44 bg-stone-200" />
                    <div className="p-5 space-y-3">
                      <div className="h-3 bg-stone-200 rounded w-1/3" />
                      <div className="h-4 bg-stone-200 rounded w-3/4" />
                      <div className="h-3 bg-stone-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
                <i className="ri-tools-line text-4xl text-charcoal/20 mb-4 block" />
                <p className="font-serif text-charcoal/40 text-lg">
                  {products.length === 0 ? "まだ教材キットが登録されていません" : "該当する教材がありません"}
                </p>
                {products.length === 0 && (
                  <Link to="/providers" className="mt-4 inline-block text-teal text-sm font-sans hover:underline cursor-pointer">事業者を探す →</Link>
                )}
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((kit, idx) => {
                  const img = kit.thumbnail_url || `https://readdy.ai/api/search-image?query=Japanese%20artisan%20craft%20tools%20materials%20professional%20kit%20minimal%20dark%20background&width=400&height=260&seq=kit-${kit.id.slice(0,8)}&orientation=landscape`;
                  return (
                    <div key={kit.id} className="bg-white rounded-2xl border border-stone-100 overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
                      style={{ animationDelay: `${idx * 60}ms` }}>
                      <Link to={`/materials/${kit.id}`} className="block relative h-44 overflow-hidden">
                        <img src={img} alt={kit.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                        {(kit.stock_count ?? 0) === 0 && kit.stock_count !== null && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <span className="bg-white/90 text-charcoal text-xs px-3 py-1 rounded-full font-sans">在庫なし</span>
                          </div>
                        )}
                        {(kit.stock_count ?? 0) > 0 && (kit.stock_count ?? 0) <= 5 && (
                          <div className="absolute top-3 right-3">
                            <span className="bg-red-400 text-white text-xs px-2 py-0.5 rounded-full font-sans">残り{kit.stock_count}点</span>
                          </div>
                        )}
                      </Link>
                      <div className="p-5">
                        <Link to={`/providers/${kit.provider_id}`} className="text-xs text-teal font-sans hover:underline cursor-pointer">
                          {getProviderName(kit)}
                        </Link>
                        <Link to={`/materials/${kit.id}`}>
                          <h3 className="font-serif text-deep-green text-base mt-1 mb-2 line-clamp-2 hover:text-teal transition-colors">{kit.title}</h3>
                        </Link>
                        <div className="flex items-center justify-between">
                          <p className="font-serif text-deep-green text-lg">¥{kit.price.toLocaleString()}</p>
                          <div className="flex gap-1">
                            <button onClick={(e) => { e.preventDefault(); openReport({ type: "kit", id: kit.id, title: kit.title }); }}
                              className="w-7 h-7 flex items-center justify-center text-charcoal/20 hover:text-red-400 cursor-pointer transition-colors" title="通報する">
                              <i className="ri-flag-line text-sm" />
                            </button>
                            <Link to={`/materials/${kit.id}`}
                              className="text-xs bg-deep-green text-white px-3 py-1.5 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
                              詳細を見る
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((kit) => {
                  const img = kit.thumbnail_url || `https://readdy.ai/api/search-image?query=Japanese%20craft%20kit%20tools%20minimal%20dark&width=160&height=90&seq=kit-l-${kit.id.slice(0,8)}&orientation=landscape`;
                  return (
                    <div key={kit.id} className="bg-white rounded-2xl border border-stone-100 p-4 flex gap-4 hover:border-teal/30 transition-colors">
                      <Link to={`/materials/${kit.id}`} className="shrink-0 w-28 h-20 rounded-xl overflow-hidden">
                        <img src={img} alt={kit.title} className="w-full h-full object-cover object-top" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Link to={`/providers/${kit.provider_id}`} className="text-xs text-teal font-sans hover:underline cursor-pointer">{getProviderName(kit)}</Link>
                            <Link to={`/materials/${kit.id}`}>
                              <h3 className="font-serif text-deep-green text-sm mt-0.5 hover:text-teal transition-colors line-clamp-2">{kit.title}</h3>
                            </Link>
                          </div>
                          <p className="font-serif text-deep-green text-base shrink-0">¥{kit.price.toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          {kit.category && <span className="text-xs text-charcoal/50 font-sans">{kit.category}</span>}
                          {(kit.stock_count ?? 0) > 0 ? (
                            <span className="text-xs text-teal font-sans">在庫あり（{kit.stock_count}点）</span>
                          ) : kit.stock_count === 0 ? (
                            <span className="text-xs text-red-400 font-sans">在庫なし</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
